import React from "react";

import { CoinIcon, FriendsBadge } from "../reward/RewardIcons";
import { formatPoint } from "../reward/pointStore";
import { productNameOf, useGroupInvite } from "../reward/GroupInvitePopup";
import { formatMonthLabel } from "../leveltest/components/LevelTestInvite";
import { doerNameOf, formatDueText, formatShortDate, WEAK_AREA_RATE } from "./homeUtils";
import "./StatusBlocks.css";

// 학생 홈 2안(2026-10-27 시안 kinder-home-v2-{english,math})의 엄마 쪽 칸들. 초등 · 중고등 홈이 함께 쓴다.
// 데이터가 없는 칸은 그리지 않는다. '학원이 한 일' 같은 서술은 데이터가 없어 두지 않는다.
// 데이터는 api/assignment/student_home.py(오늘 학원에서 · 단어 시험 · 약한 곳 · 풀이 사진)와 레벨테스트 API.

const STAGE_COUNT = 12;
const isSameDay = (value, base = new Date()) => {
  if (!value) return false;
  const date = new Date(value);
  return date.getFullYear() === base.getFullYear() && date.getMonth() === base.getMonth() && date.getDate() === base.getDate();
};
const formatClock = (value) => {
  const date = new Date(value);
  const hour = date.getHours();
  return `${hour < 12 ? "오전" : "오후"} ${hour % 12 || 12}:${String(date.getMinutes()).padStart(2, "0")}`;
};
const formatStage = (stage) => `S${String(stage).padStart(2, "0")}`;

// 성을 뗀 이름(지우). 한 글자 · 두 글자 이름은 그대로
export const givenNameOf = (fullName) => {
  const name = String(fullName || "").trim();
  return name.length >= 3 ? name.slice(1) : name;
};

const buildTodayRowList = (today) => {
  if (!today) return [];
  const rowList = [];
  const attendanceList = [];
  if (today.check_in) attendanceList.push(`등원 ${formatClock(today.check_in)}`);
  if (today.check_out) attendanceList.push(`하원 ${formatClock(today.check_out)}`);
  if (!attendanceList.length && today.attendance !== null && today.attendance !== undefined) {
    attendanceList.push(today.attendance ? "출석" : "결석");
  }
  if (today.attitude) attendanceList.push(`수업 태도 ${today.attitude}`);
  if (attendanceList.length) rowList.push({ label: "출결", text: attendanceList.join(" · ") });
  if (today.lesson_list?.length) rowList.push({ label: "수업", text: today.lesson_list.join(" · ") });
  if (today.exam_list?.length) {
    rowList.push({
      label: "시험",
      text: today.exam_list
        .map((exam) => (exam.score !== null && exam.score !== undefined && exam.score !== "" ? `${exam.title} ${exam.score}/${exam.total_score}` : exam.title))
        .join(" · "),
    });
  }
  if (today.homework_list?.length) rowList.push({ label: "숙제", text: today.homework_list.join(" · ") });
  if (today.comment) rowList.push({ label: "선생님", text: today.comment });
  return rowList;
};

const wordDeltaTextOf = (wordTest) => {
  const previous = wordTest.previous_right_count;
  if (previous === null || previous === undefined) return "";
  const diff = wordTest.right_count - previous;
  if (diff > 0) return `지난번 ${previous}개보다 ${diff}개 더 맞혔어요`;
  if (diff < 0) return `지난번 ${previous}개보다 ${-diff}개 덜 맞혔어요`;
  return `지난번과 같이 ${previous}개 맞혔어요`;
};

// 오늘 학원에서 + 단어 시험. 둘 다 없으면 그리지 않는다
export const TodayCard = ({ studentName, today, wordTest, onOpenWordTest }) => {
  const rowList = buildTodayRowList(today);
  if (!rowList.length && !wordTest) return null;
  const isTodayTest = wordTest && isSameDay(wordTest.finished_at);
  const deltaText = wordTest ? wordDeltaTextOf(wordTest) : "";

  return (
    <section className="home-card home-today">
      {rowList.length > 0 && (
        <>
          <h2 className="home-today__title">{givenNameOf(studentName)} 오늘 학원에서</h2>
          <div className="home-today__log">
            {rowList.map((row) => (
              <div key={row.label} className="home-today__row">
                <span className="home-today__label">{row.label}</span>
                <span className="home-today__text">{row.text}</span>
              </div>
            ))}
          </div>
        </>
      )}
      {wordTest && (
        <button type="button" className="home-word-test" onClick={onOpenWordTest}>
          <span className="home-word-test__score">
            {wordTest.right_count}
            <small>/{wordTest.total_count}</small>
          </span>
          <span className="home-word-test__body">
            <b>
              {isTodayTest ? "오늘 단어 시험" : `단어 시험 · ${formatShortDate(wordTest.finished_at)}`} · {wordTest.title}
            </b>
            {deltaText && <span className="home-word-test__delta">{deltaText}</span>}
            {wordTest.wrong_word_list.length > 0 && (
              <span className="home-word-test__miss">
                {wordTest.wrong_word_list.map((word) => (
                  <em key={word} lang="en">
                    {word}
                  </em>
                ))}
                {wordTest.wrong_word_count > wordTest.wrong_word_list.length && (
                  <em className="is-more">+{wordTest.wrong_word_count - wordTest.wrong_word_list.length}</em>
                )}
              </span>
            )}
          </span>
        </button>
      )}
    </section>
  );
};

// 레벨: S01~S12 띠 위에 지금 자리. 리포트 · 이번 호 응시하기
export const LevelBandCard = ({ studentName, levelTest }) => {
  if (!levelTest) return null;
  if (levelTest.demo_report_url) {
    return (
      <section className="home-card">
        <div className="home-card__head">
          <h2>{givenNameOf(studentName)} 레벨</h2>
          <a className="home-link" href={levelTest.demo_report_url} target="_blank" rel="noreferrer">
            리포트 예시 ›
          </a>
        </div>
      </section>
    );
  }
  const latest = levelTest.linked ? levelTest.latest : null;
  const invite = levelTest.invite;
  if (!latest) {
    if (!invite || !invite.test_url) return null;
    return (
      <section className="home-card">
        <div className="home-card__head">
          <h2>{givenNameOf(studentName)} 레벨</h2>
        </div>
        <a className="home-go home-level__invite" href={invite.test_url} target="_blank" rel="noreferrer">
          {formatMonthLabel(invite.month)} 호 레벨테스트 응시하기
        </a>
      </section>
    );
  }

  const finishedList = (levelTest.results || []).filter((result) => result.is_finished);
  const previous = finishedList.find((result) => result.session_id !== latest.session_id);
  let changeText = "";
  if (previous && previous.stage !== latest.stage) {
    changeText = previous.stage < latest.stage ? `지난번 ${formatStage(previous.stage)} 에서 올랐어요` : `지난번 ${formatStage(previous.stage)}`;
  }
  const stage = Math.max(1, Math.min(STAGE_COUNT, Number(latest.stage) || 1));
  const areaList = latest.areas || [];

  return (
    <section className="home-card">
      <div className="home-card__head">
        <h2>{givenNameOf(studentName)} 레벨</h2>
        {latest.report_url && (
          <a className="home-link" href={latest.report_url} target="_blank" rel="noreferrer">
            레벨테스트 리포트 ›
          </a>
        )}
      </div>
      <div className="home-level">
        <div className="home-level__badge">
          <small>STAGE</small>
          <b>{formatStage(stage)}</b>
        </div>
        <div className="home-level__body">
          <div className="home-level__title">{latest.stage_name || `${latest.score_percent}점`}</div>
          <div className="home-level__desc">
            {latest.taken_month.replace("-", ". ")} · {latest.score_percent}점{changeText ? ` · ${changeText}` : ""}
          </div>
          <div className="home-level__scale">
            {Array.from({ length: STAGE_COUNT }, (_, index) => (
              <i key={index} className={index + 1 < stage ? "is-on" : index + 1 === stage ? "is-now" : ""} />
            ))}
          </div>
          <div className="home-level__scale-label">
            <span>S01</span>
            <span>S12</span>
          </div>
        </div>
      </div>
      {areaList.length > 0 && (
        <div className="home-level__areas">
          {areaList.map((area) => (
            <span key={area.area} className={area.pct < WEAK_AREA_RATE ? "is-weak" : ""}>
              {area.area} <b>{area.pct}</b>
            </span>
          ))}
        </div>
      )}
      {invite && invite.test_url && (
        <a className="home-go home-go--ghost home-level__invite" href={invite.test_url} target="_blank" rel="noreferrer">
          {formatMonthLabel(invite.month)} 호 응시하기
        </a>
      )}
    </section>
  );
};

// 약한 곳: 맞힌 비율이 낮은 유형과, 그 유형이 나온 과제에 붙은 복습 과제
export const WeakCard = ({ weakList, onOpenRetry }) => {
  if (!weakList || !weakList.length) return null;
  return (
    <section className="home-card">
      <div className="home-card__head">
        <h2>약한 곳</h2>
        <span className="home-card__sub">최근 과제</span>
      </div>
      <div className="home-weak">
        {weakList.map((area) => (
          <div key={area.name} className="home-weak__item">
            <div className="home-weak__head">
              <span className="home-weak__name">{area.name}</span>
              <span className="home-weak__rate">{area.rate}%</span>
            </div>
            <div className="home-weak__track">
              <i style={{ width: `${Math.max(area.rate, 2)}%` }} />
            </div>
            {area.retry_list.length > 0 && (
              <div className="home-weak__plan">
                {area.retry_list.map((retry) => (
                  <button
                    key={retry.assignment_id}
                    type="button"
                    className={`home-weak__retry${retry.is_finished ? " is-done" : ""}`}
                    onClick={() => onOpenRetry(retry)}
                  >
                    <span className="home-weak__check" />
                    <b>복습 · {retry.title}</b>
                    <span className="home-weak__when">
                      {retry.is_finished
                        ? `다 했어요${retry.score !== null && retry.score !== undefined ? ` · ${retry.score}점` : ""}`
                        : `${retry.progress_rate}%${retry.closed_at ? ` · ${formatDueText(retry.closed_at)}까지` : ""}`}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

// 단어 시험 점수: 최근 8회 막대
export const WordTrendCard = ({ trendList }) => {
  if (!trendList || trendList.length < 2) return null;
  // 점수 차이가 보이게 가장 낮은 점수 아래부터 막대를 세운다(0 부터 세우면 70 과 90 이 거의 같아 보인다)
  const scoreList = trendList.map((point) => Number(point.score) || 0);
  const lowest = Math.max(0, Math.min(...scoreList) - 10);
  const highest = Math.max(...scoreList);
  const heightOf = (score) => 20 + (80 * (score - lowest)) / Math.max(highest - lowest, 1);
  return (
    <section className="home-card">
      <div className="home-card__head">
        <h2>단어 시험 점수</h2>
        <span className="home-card__sub">최근 {trendList.length}회</span>
      </div>
      <div className="home-trend">
        {trendList.map((point, index) => (
          <div key={point.assignment_id} className={`home-trend__bar${index === trendList.length - 1 ? " is-last" : ""}`}>
            <b>{point.score}</b>
            <i style={{ height: `${heightOf(Number(point.score) || 0)}%` }} />
            <span>{isSameDay(point.finished_at) ? "오늘" : formatShortDate(point.finished_at)}</span>
          </div>
        ))}
      </div>
    </section>
  );
};

// '○○가 할 것' 맨 위: 같이 사기 초대 · 풀이 사진 보너스. 없으면 아무것도 그리지 않는다
export const KidTopCards = ({ studentName, photoBonus, onOpenPhotoBonus }) => {
  const { invite, answer, message, isBusy } = useGroupInvite();
  if (!invite && !photoBonus) return null;
  return (
    <>
      <div className="home-divider">
        <span>{doerNameOf(studentName)} 할 것</span>
      </div>
      {invite && (
        <section className="home-bm">
          <span className="home-bm__badge">
            <FriendsBadge size={76} />
          </span>
          <div className="home-bm__body">
            <div className="home-bm__title">
              <b>{invite.starter_name}</b> 친구가 같이 사자고 해요
            </div>
            <div className="home-bm__product">{productNameOf(invite.product)}</div>
            <div className="home-bm__split">
              {invite.member_list.map((member) => (
                <span key={member.name}>
                  {member.name} {formatPoint(member.share_points)}
                </span>
              ))}
            </div>
            {message && <div className="home-bm__message">{message}</div>}
          </div>
          <div className="home-bm__side">
            <div className="home-bm__price">
              <CoinIcon size="1em" />
              <span>내 몫</span> {formatPoint(invite.my_share)}
            </div>
            <button type="button" className="home-bm__button" disabled={isBusy} onClick={() => answer(true)}>
              같이 살래요
            </button>
            <button type="button" className="home-bm__ghost" disabled={isBusy} onClick={() => answer(false)}>
              안 할래요
            </button>
          </div>
        </section>
      )}
      {photoBonus && (
        <section className="home-bm">
          <div className="home-bm__body">
            <div className="home-bm__title">
              풀이 사진 올리고 선생님께 <b>잘함</b> 받으면
            </div>
            <div className="home-bm__product">{photoBonus.title}</div>
          </div>
          <div className="home-bm__side">
            <div className="home-bm__price">
              <CoinIcon size="1em" />+{formatPoint(photoBonus.bonus_points)}
            </div>
            <button type="button" className="home-bm__button" onClick={onOpenPhotoBonus}>
              풀이 사진 올리기
            </button>
          </div>
        </section>
      )}
    </>
  );
};

// 영어 · 수학을 둘 다 하는 아이만 위에 탭이 뜬다. 포인트는 같이 쓴다
export const SubjectTabs = ({ subject, onChange }) => (
  <nav className="home-subject">
    {[
      ["english", "영어"],
      ["math", "수학"],
    ].map(([value, text]) => (
      <button
        key={value}
        type="button"
        className={subject === value ? "is-on" : ""}
        onClick={() => onChange(value)}
      >
        {text}
      </button>
    ))}
  </nav>
);
